import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, Globe, Fingerprint, Wallet2, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PageHeader, StatCard, fmtUSD, fmtDate } from "@/components/ui-bits";

export default function Profile() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };

  const rows = [
    { Icon: Fingerprint, label: "User ID", value: user?.user_id || "—", mono: true, accent: true },
    { Icon: User, label: "Full Name", value: user?.name || "—" },
    { Icon: Mail, label: "Email", value: user?.email || "—" },
    { Icon: Phone, label: "Mobile", value: user?.phone || "—" },
    { Icon: Globe, label: "Country", value: user?.country || "—" },
  ];

  return (
    <div>
      <PageHeader title="Profile" subtitle={`Member since ${fmtDate(user?.created_at)}`} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Wallet Balance" value={fmtUSD(user?.wallet_balance)} accent />
        <StatCard label="Reward Balance" value={fmtUSD(user?.rewards_balance)} />
        <StatCard label="Total Withdrawn" value={fmtUSD(user?.total_withdrawn)} />
        <StatCard label="Total Rewards Earned" value={fmtUSD(user?.total_rewards_earned)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="glass p-5 lg:col-span-2" data-testid="profile-details">
          <h3 className="font-display text-lg font-bold mb-4">Account Details</h3>
          <div className="divide-y divide-white/5">
            {rows.map(({ Icon, label, value, mono, accent }) => (
              <div key={label} className="flex items-center gap-3 py-3">
                <div className="w-9 h-9 rounded-lg bg-[#0A132A] border border-white/10 flex items-center justify-center flex-shrink-0">
                  <Icon size={16} className="text-[#00F0FF]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] font-bold">{label}</div>
                  <div className={`mt-0.5 break-all ${mono ? "font-mono" : ""} ${accent ? "text-[#00F0FF] font-bold" : "text-white"}`}>{value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass p-5">
          <h3 className="font-display text-lg font-bold mb-4">Wallet & Security</h3>
          <div className="text-xs uppercase tracking-[0.18em] text-[#475569] font-bold mb-1">BEP20 Withdrawal Wallet</div>
          <div className="font-mono text-sm bg-[#060B19] border border-white/10 rounded-lg px-3 py-2.5 break-all mb-4" data-testid="profile-wallet">
            {user?.wallet_address || "Not set"}
          </div>
          <Link to="/wallet" data-testid="profile-manage-wallet"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-[#121E3D] border border-white/10 hover:border-[#00F0FF]/40 transition-all mb-3">
            <Wallet2 size={16} className="text-[#00F0FF]" /> Manage Wallet
          </Link>
          <button onClick={doLogout} data-testid="profile-logout"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20 transition-all">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </div>
    </div>
  );
}
